/**
 * Mini XState Machine for Payment Testing in DevTool
 *
 * Flow:
 * 1. idle → creating payment intent
 * 2. payment intent created → processing POS payment (via actor)
 * 3. processing → polling reader status
 * 4. complete → success/failure
 */

import {
  cancelPOSPaymentByReaderId,
  getTerminalByReaderId,
  processPOSPaymentByReaderId,
} from "@/app/directories/kiosk/server-actions/stripe-actions";
import type { Stripe } from "stripe";
import { assign, fromPromise, setup } from "xstate";
import {
  type PaymentIntentResponse,
  createPaymentIntent,
} from "./stripe-payment-helpers";
import type {
  ProcessPOSPaymentInput,
  ProcessPOSPaymentSuccess,
} from "./stripe-payment-types";

// Context
interface PaymentTestContext {
  // Input
  amount: number;
  readerId: string | null;
  orgId: number | null;

  // Payment Intent
  paymentIntentId: string | null;
  clientSecret: string | null;

  // Reader Status
  readerStatus: "idle" | "processing" | "succeeded" | "failed" | null;

  // Error
  error: {
    code: string;
    message: string;
    retryable?: boolean;
  } | null;
}

// Input type - more flexible, allows partial initialization
type PaymentTestInput = Partial<PaymentTestContext>;

// Events
type PaymentTestEvent =
  | { type: "START_TEST"; amount: number }
  | { type: "RETRY" }
  | { type: "RESET" }
  | { type: "CANCEL" };

// Type state
type PaymentTestState =
  | { value: "idle"; context: PaymentTestContext }
  | { value: "creatingPaymentIntent"; context: PaymentTestContext }
  | { value: "processingPayment"; context: PaymentTestContext }
  | { value: "canceling"; context: PaymentTestContext }
  | { value: "canceled"; context: PaymentTestContext }
  | { value: "success"; context: PaymentTestContext }
  | { value: "failure"; context: PaymentTestContext };

/**
 * Actor: Create Payment Intent
 */
const createPaymentIntentActor = fromPromise(
  async ({
    input,
  }: {
    input: { amount: number; orgId: number | null };
  }): Promise<PaymentIntentResponse> => {
    // Validate inputs
    if (!input.orgId) {
      throw new Error("Organization ID is required to create payment intent");
    }

    if (input.amount <= 0) {
      throw new Error("Payment amount must be greater than 0");
    }

    const result = await createPaymentIntent({
      amount: input.amount,
      orgId: input.orgId,
    });

    console.log(
      `[PaymentTestMachine] Created payment intent ${result.paymentIntentId} with client secret ${result.clientSecret}`,
    );

    return result;
  },
);

/**
 * Actor: Cancel POS Payment
 */
const cancelPOSPaymentActor = fromPromise(
  async ({
    input,
  }: {
    input: { readerId: string | null };
  }): Promise<{ status: string }> => {
    // Validate input
    if (!input.readerId) {
      // If no reader ID, just return success (nothing to cancel)
      return { status: "SUCCESS" };
    }

    console.log(
      `[PaymentTestMachine] Cancelling payment on reader ${input.readerId}`,
    );

    const result = await cancelPOSPaymentByReaderId(input.readerId);
    return result;
  },
);

/**
 * Actor: Process POS Payment (wrapper for the real actor logic)
 */
const processPOSPaymentActor = fromPromise(
  async ({
    input,
  }: {
    input: ProcessPOSPaymentInput;
  }): Promise<ProcessPOSPaymentSuccess> => {
    // Validate inputs
    if (!input.readerId) {
      throw new Error("Reader ID is required to process payment");
    }

    if (!input.paymentIntentId) {
      throw new Error("Payment Intent ID is required to process payment");
    }

    // Step 1: Initiate payment on reader
    console.log(
      `[PaymentTestMachine] Starting payment on reader ${input.readerId}`,
    );
    await processPOSPaymentByReaderId(input.paymentIntentId, input.readerId);

    // Step 2: Poll reader status until payment completes
    console.log(
      `[PaymentTestMachine] Polling reader status every ${
        input.pollInterval || 1000
      }ms`,
    );

    const startTime = Date.now();
    const timeout = input.timeout || 120000;
    const pollInterval = input.pollInterval || 1000;

    while (Date.now() - startTime < timeout) {
      const stringifiedReader = await getTerminalByReaderId(input.readerId);

      if (!stringifiedReader) {
        throw new Error(`Reader ${input.readerId} not found`);
      }

      const reader = stringifiedReader as Stripe.Terminal.Reader;

      // Check if reader is offline
      if (reader.status !== "online") {
        throw new Error(`Reader ${input.readerId} is ${reader.status}`);
      }

      // Check action status
      if (reader.action?.status === "succeeded") {
        return {
          status: "succeeded",
          paymentIntentId: input.paymentIntentId,
          reader,
        };
      }

      if (reader.action?.status === "failed") {
        throw new Error(
          reader.action?.failure_message || "Payment failed on terminal reader",
        );
      }

      // Action was canceled
      if (!reader.action) {
        throw new Error("Payment was canceled on terminal reader");
      }

      // Still in progress, wait and poll again
      await new Promise((resolve) => setTimeout(resolve, pollInterval));
    }

    // Timeout reached
    throw new Error(`Payment polling timeout after ${timeout}ms`);
  },
);

/**
 * Payment Test Machine
 */
export const paymentTestMachine = setup({
  types: {
    context: {} as PaymentTestContext,
    events: {} as PaymentTestEvent,
    input: {} as PaymentTestInput,
  },
  actors: {
    createPaymentIntent: createPaymentIntentActor,
    processPOSPayment: processPOSPaymentActor,
    cancelPOSPayment: cancelPOSPaymentActor,
  },
}).createMachine({
  id: "paymentTest",
  initial: "idle",
  context: ({ input }) => ({
    amount: input?.amount ?? 1000,
    readerId: input?.readerId ?? null,
    orgId: input?.orgId ?? null,
    paymentIntentId: input?.paymentIntentId ?? null,
    clientSecret: input?.clientSecret ?? null,
    readerStatus: input?.readerStatus ?? null,
    error: input?.error ?? null,
  }),
  states: {
    idle: {
      on: {
        START_TEST: {
          target: "creatingPaymentIntent",
          actions: assign({
            amount: ({ event }) => event.amount,
            error: null,
            paymentIntentId: null,
            clientSecret: null,
            readerStatus: null,
          }),
        },
      },
    },

    creatingPaymentIntent: {
      invoke: {
        src: "createPaymentIntent",
        input: ({ context }) => ({
          amount: context.amount,
          orgId: context.orgId,
        }),
        onDone: {
          target: "processingPayment",
          actions: assign({
            paymentIntentId: ({ event }) => event.output.paymentIntentId,
            clientSecret: ({ event }) => event.output.clientSecret,
            readerStatus: "idle",
          }),
        },
        onError: {
          target: "failure",
          actions: assign({
            error: ({ event }) => {
              const err = event.error as { code?: string; message?: string };
              return {
                code: err.code || "UNKNOWN_ERROR",
                message: err.message || "Failed to create payment intent",
                retryable: true,
              };
            },
          }),
        },
      },
    },

    processingPayment: {
      invoke: {
        src: "processPOSPayment",
        input: ({ context }) => ({
          paymentIntentId: context.paymentIntentId,
          readerId: context.readerId,
          pollInterval: 1000,
          timeout: 120000, // 2 minutes
        }),
        onDone: {
          target: "success",
          actions: assign({
            readerStatus: "succeeded",
          }),
        },
        onError: {
          target: "failure",
          actions: assign({
            readerStatus: "failed",
            error: ({ event }) => {
              const err = event.error as {
                code?: string;
                message?: string;
                retryable?: boolean;
              };
              return {
                code: err.code || "PAYMENT_FAILED",
                message: err.message || "Payment processing failed",
                retryable: err.retryable ?? true,
              };
            },
          }),
        },
      },
      on: {
        CANCEL: {
          target: "canceling",
        },
      },
    },

    canceling: {
      invoke: {
        src: "cancelPOSPayment",
        input: ({ context }) => ({
          readerId: context.readerId,
        }),
        onDone: {
          target: "canceled",
          actions: assign({
            readerStatus: null,
          }),
        },
        onError: {
          target: "failure",
          actions: assign({
            error: ({ event }) => {
              const err = event.error as {
                code?: string;
                message?: string;
              };
              return {
                code: err.code || "CANCEL_FAILED",
                message: err.message || "Failed to cancel payment",
                retryable: false,
              };
            },
          }),
        },
      },
    },

    canceled: {
      on: {
        RESET: {
          target: "idle",
          actions: assign({
            amount: 1000,
            paymentIntentId: null,
            clientSecret: null,
            readerStatus: null,
            error: null,
          }),
        },
      },
    },

    success: {
      on: {
        RESET: {
          target: "idle",
          actions: assign({
            amount: 1000,
            paymentIntentId: null,
            clientSecret: null,
            readerStatus: null,
            error: null,
          }),
        },
      },
    },

    failure: {
      on: {
        RETRY: {
          target: "creatingPaymentIntent",
          actions: assign({
            error: null,
          }),
        },
        RESET: {
          target: "idle",
          actions: assign({
            amount: 1000,
            paymentIntentId: null,
            clientSecret: null,
            readerStatus: null,
            error: null,
          }),
        },
      },
    },
  },
});
